import { ChartConfig } from '@shared/components/ui/chart';

/**
 * Builds the data, config and legend for an "Emissions by organisation"
 * stacked-area chart from a group dashboard's emissions-over-time series.
 *
 * Organisations are ranked by total emissions across the whole series; the top
 * {@link MAX_NAMED_ORGS} get their own coloured band (darkest = largest) and the
 * remaining long tail is aggregated into a single "Other (N)" band.
 */

/** Sequential teal scale, darkest first; see --chart-org-* in globals.css. */
const ORG_SERIES_COLORS = [
  'var(--chart-org-1)',
  'var(--chart-org-2)',
  'var(--chart-org-3)',
  'var(--chart-org-4)',
  'var(--chart-org-5)',
  'var(--chart-org-6)',
];
const OTHER_COLOR = 'var(--chart-org-other)';
const MAX_NAMED_ORGS = ORG_SERIES_COLORS.length;

/** One period's per-organisation rows, already labelled for the X axis. */
export interface OrgSeriesPoint {
  label: string;
  organisations: readonly {
    name: string;
    totalEmissionsTonnes: number | null;
  }[];
}

export interface OrgEmissionsSeries {
  /** One row per period: `{ label, [orgName]: tonnes, 'Other (N)': tonnes }`. */
  chartData: Record<string, number | string>[];
  /** Series config keyed by organisation name, in stacking order (bottom first). */
  chartConfig: ChartConfig;
  /** Legend items in the same order as the stacked bands. */
  legend: { label: string; color: string }[];
}

export function buildOrgEmissionsSeries(
  points: readonly OrgSeriesPoint[]
): OrgEmissionsSeries {
  // Rank organisations by total emissions across every period.
  const totals = new Map<string, number>();
  for (const point of points) {
    for (const org of point.organisations) {
      totals.set(
        org.name,
        (totals.get(org.name) ?? 0) + (org.totalEmissionsTonnes ?? 0)
      );
    }
  }

  const ranked = [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
  const namedOrgs = ranked.slice(0, MAX_NAMED_ORGS);
  const namedSet = new Set(namedOrgs);
  const otherCount = ranked.length - namedOrgs.length;
  const hasOther = otherCount > 0;
  const otherKey = `Other (${otherCount})`;

  const chartData = points.map((point) => {
    // Missing orgs default to 0 so stacked bands stay continuous over time.
    const row: Record<string, number | string> = { label: point.label };
    for (const name of namedOrgs) row[name] = 0;
    if (hasOther) row[otherKey] = 0;

    for (const org of point.organisations) {
      const value = org.totalEmissionsTonnes ?? 0;
      const key = namedSet.has(org.name)
        ? org.name
        : hasOther
          ? otherKey
          : null;
      if (key) row[key] = (row[key] as number) + value;
    }
    return row;
  });

  const chartConfig: ChartConfig = {};
  namedOrgs.forEach((name, index) => {
    chartConfig[name] = { label: name, color: ORG_SERIES_COLORS[index] };
  });
  if (hasOther) chartConfig[otherKey] = { label: otherKey, color: OTHER_COLOR };

  const legend = [
    ...namedOrgs.map((name, index) => ({
      label: name,
      color: ORG_SERIES_COLORS[index],
    })),
    ...(hasOther ? [{ label: otherKey, color: OTHER_COLOR }] : []),
  ];

  return { chartData, chartConfig, legend };
}
